const R = require('ramda');
const { failProofAssign } = require('../utils');
const utils = require('../utils/utils');
const { STATUS_PRIORITY } = require('../constants');
const ArchitectureElement = require('./ArchitectureElement');
const { getMetricAndConversionFromReport } = require('../utils/metricUtils');

/** @type {AdSet} */
class AdSet extends ArchitectureElement {
  constructor(obj) {
    super();
    this.updateData(obj);
  }

  updateData(obj) {
    if (!obj) return;
    obj.channelStatus = obj.status;
    failProofAssign(this, R.omit(['status', 'metric', 'ads', 'campaign', 'channel'], obj));
  }

  get level() {
    return 'adSet';
  }

  set subChannels(subChannels) {
    this._subChannels = subChannels;
  }

  get subChannels() {
    if (this._subChannels) return this._subChannels;
    return [this.channel];
  }

  // get status() {
  //     if (this.channelStatus !== 'RUNNING') return this.channelStatus;
  //     if (this.campaign.channelStatus !== 'RUNNING')
  //         return this.campaign.channelStatus;
  //     const adsStatuses = this.ads.map(R.prop('status'));
  //     if (adsStatuses.length === 0) return 'PAUSED';
  //     for (const status of R.reverse(STATUS_PRIORITY)) {
  //         if (adsStatuses.some(R.equals(status))) return status;
  //     }
  //     return this.channelStatus;
  // }

  // get metric() {
  //   return utils.getMetricFromList(this.ads);
  // }

  // get reportsPerDate() {
  //   const reports = this.ads
  //     .map(ad => ad.reportsPerDate)
  //     .flat()
  //     .filter(Boolean);

  //   const mergedReports = utils.mergeReportArrayByDate(reports);
  //   return mergedReports;
  // }

  // getConversionGoalMap() {
  //   const conversionGoalMaps = this.ads.map(ad => ad.getConversionGoalMap());
  //   return utils.unionNameToKeyMap(conversionGoalMaps);
  // }

  // getConversionsByTracker() {
  //   const adsConversionsByTrackers = this.ads
  //     .map(ad => ad.getConversionsByTracker())
  //     .filter(Boolean);
  //   return utils.mergeConversionsByTracker(adsConversionsByTrackers);
  // }

  getConversionsSummaryByTrackerPerDate(channel) {
    let conversionByTrackerPerDates = this.getConversionsByTrackerPerDate();
    if (!conversionByTrackerPerDates || R.isEmpty(conversionByTrackerPerDates))
      conversionByTrackerPerDates = this.ads
        .map(ad => ad.getConversionsByTrackerPerDate({ channel }))
        .flat()
        .filter(Boolean);
    return utils.mergeConversionsByTrackerPerDate(conversionByTrackerPerDates);
  }

  get resultIndicator() {
    const OBJECTIVE_TO_RESULT_INDICATOR = {
      LINK_CLICKS: 'clicks',
      APP_INSTALLS: 'installs',
      LEAD_FORM: 'leads',
    };
    if (OBJECTIVE_TO_RESULT_INDICATOR[this.campaign.objective]) {
      return OBJECTIVE_TO_RESULT_INDICATOR[this.campaign.objective];
    }
    if (this.conversionGoal) {
      return this.conversionGoal;
    }
    return 'clicks';
  }

  get results() {
    if (this.metric.conversions != null) return this.metric.conversions;
    return this.metric.clicks;
  }

  get actions() {
    const adsActions = R.compose(
      utils.mergeActions,
      R.flatten,
      R.reject(el => R.or(R.isNil(el), R.isEmpty(el))),
      R.map(R.path(['report', 'actions'])),
    )(this.ads);
    return adsActions;
  }

  get children() {
    return this.ads;
  }

  get conversion() {
    if (this.conversionGoal) {
      return this.actions.find(action => action.key === this.conversionGoal);
    }
    return null;
  }

  get isConversion() {
    return Boolean(this.conversionGoal) || this.campaign.isConversion;
  }

  get newAndReturning() {
    const text = this.isRemarketing ? 'returning' : 'new';
    return [
      {
        text,
        metric: this.metric,
      },
    ];
  }

  getActionsWithSpend() {
    const adsActions = R.compose(
      utils.mergeActions,
      R.flatten,
      R.reject(el => R.or(R.isNil(el), R.isEmpty(el))),
      R.map(ad => {
        const spendOnActions = R.path(['report', 'metric', 'spend'])(ad);
        const actions = (R.path(['report', 'actions'])(ad) || []).map(action => {
          return {
            conversions: action.value,
            spend: spendOnActions,
            ...action,
          };
        });
        return actions;
      }),
    )(this.ads);

    return adsActions;
  }

  getAllConversions() {
    let conversions = utils.getTrackerConversions(this.actions());
    const click = this.metric.clicksOnActions;
    const spend = this.metric.spendOnActions;
    return utils.attachClickAndSpendToConversions(conversions, {
      click,
      spend,
    });
  }

  get bidCap() {
    if (this.bidAmount != null) return this.bidAmount;
    return this?.campaign?.bidCap;
  }

  get bidStrategies() {
    if (this.bidStrategy) {
      return [this.bidStrategy];
    }
    return this?.campaign?.bidStrategy;
  }

  get objectives() {
    return this?.campaign?.objectives;
  }

  get targetingType() {
    let type = 'interest';
    if (this.channel === 'google') {
      type = 'keywords_audiences';
    }
    if (this.isRemarketing) {
      type = 'remarketing';
    } else if (this.isLookalike) {
      type = 'lookalike';
    }
    return type;
  }

  format({ includeChildren = false } = {}) {
    const obj = super.format({ includeChildren });
    return obj;
  }

  // format() {
  //     return {
  //         ads: this.ads.map((ad) => ad.format()),
  //         ...R.pick(
  //             [
  //                 'id',
  //                 'channelId',
  //                 'name',
  //                 'status',
  //                 'channelStatus',
  //                 'schedule',
  //                 'metric',
  //                 'bidCap',
  //                 'targetingType',
  //                 'bidStrategies',
  //                 'objectives',
  //                 'campaignId',
  //                 'placements',
  //                 'level',
  //             ],
  //             this
  //         ),
  //         budget: this.formattedBudget,
  //         subChannels: this.subChannels,
  //         conversionEvents: this.conversionGoal
  //             ? [this.conversionGoal]
  //             : null,
  //         conversionsByTracker: this.getConversionsByTracker(),
  //     };
  // }

  getJSON() {
    const adIds = this.getChildElementIds();
    const obj = {
      ...R.omit(['ads', 'campaign', 'architecture', '_subChannels', 'FEFormat'], this),
      status: this.status,
      subChannels: this.subChannels,
      adIds,
    };
    obj.metric = this.metric;
    obj.conversionsByTracker = this.getConversionsByTracker();
    obj.createdAt = this.createdAt;
    let getFormattedReports = reports => {
      return reports.map(report => getMetricAndConversionFromReport({ report, element: this }));
    };
    obj.reportsPerMonth = getFormattedReports(this.getReports({ dateGroup: 'month' }));
    obj.reportsPerWeek = getFormattedReports(this.getReports({ dateGroup: 'week' }));
    obj.reportsPerDay = getFormattedReports(this.getReports({ dateGroup: 'day' }));
    obj.level = this.level;

    return obj;
  }
}

module.exports = AdSet;
